import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPatch, apiPost } from "./api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Eye,
  Loader2,
  Megaphone,
  MousePointerClick,
  Pencil,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Link } from "wouter";
import AdminPageLayout from "./AdminPageLayout";

const PLACEMENTS = [
  ["feed", "콘텐츠 피드"],
  ["homepage_top", "홈 상단"],
  ["sidebar", "사이드바"],
  ["article_top", "기사 상단"],
  ["article_bottom", "기사 하단"],
  ["mobile_bottom", "모바일 하단"],
  ["chat-start", "채팅 시작"],
  ["chat-room-top", "채팅방 상단"],
  ["chat-list-top", "채팅 목록 상단"],
  ["chat-list-bottom", "채팅 목록 하단"],
] as const;
const REGIONS = ["ALL", "NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

interface Ad {
  id: string;
  title: string;
  advertiser_name: string;
  advertiser_id: number | null;
  business_name: string | null;
  business_id: string | null;
  contact_email: string | null;
  image_url: string;
  target_url: string;
  placement: string;
  region: string;
  start_date: string;
  end_date: string;
  active: number;
  cpc_price: number;
  click_count: number;
  impressions: number;
  impressions_7d: number;
  clicks_7d: number;
  tags_json: string;
  priority: number;
  review_status: "pending" | "approved" | "rejected";
  billing_status: "untracked" | "unpaid" | "paid" | "waived" | "refunded";
  admin_notes: string | null;
  last_event_at: string | null;
  delivery_status: string;
  issues: string[];
  created_at: string;
}

interface AdStats {
  totalClicks: number;
  totalImpressions: number;
  averageCTR: number;
  totalAds: number;
  deliveringAds: number;
  pendingReview: number;
  expiredActive: number;
  zeroDelivery: number;
  paidWithoutAds: number;
  paymentRiskAds: number;
  byPlacement: Array<{ name: string; ads: number; active: number; impressions: number; clicks: number }>;
  byRegion: Array<{ name: string; ads: number; active: number; impressions: number; clicks: number }>;
}

type AdFilter = "all" | "delivering" | "attention" | "pending" | "paused";

const deliveryLabels: Record<string, string> = {
  delivering: "정상 송출",
  ready_no_delivery: "노출 0 — 점검",
  scheduled: "시작 예정",
  expired: "기간 만료",
  paused: "일시 중지",
  pending_review: "검토 대기",
  rejected: "거절",
  needs_attention: "설정 오류",
};
const issueLabels: Record<string, string> = {
  invalid_target_url: "목적지 URL 오류",
  invalid_image_url: "이미지 URL 오류",
  invalid_placement: "지면 오류",
  invalid_region: "지역 오류",
  invalid_period: "기간 오류",
  expired_but_active: "만료 후 활성 상태",
  active_without_approval: "미승인 활성 상태",
};

function deliveryBadge(status: string) {
  const variant = status === "delivering" ? "default" : status === "paused" || status === "scheduled" ? "secondary" : "destructive";
  return <Badge variant={variant}>{deliveryLabels[status] || status}</Badge>;
}

function formatNumber(value: unknown) {
  return Number(value || 0).toLocaleString("en-AU");
}

export default function AdsManagement() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [stats, setStats] = useState<AdStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [filter, setFilter] = useState<AdFilter>("all");
  const [mutationId, setMutationId] = useState<string | null>(null);
  const { toast } = useToast();

  const loadData = async (background = false) => {
    background ? setRefreshing(true) : setLoading(true);
    try {
      const [adsData, statsData] = await Promise.all([
        apiGet<{ ads: Ad[] }>("/api/admin/ads"),
        apiGet<AdStats>("/api/admin/ads/stats"),
      ]);
      setAds(adsData.ads || []);
      setStats(statsData);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "광고 운영 데이터를 불러오지 못했습니다.";
      setError(message);
      toast({ title: "광고 데이터 오류", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { void loadData(); }, []);

  const attentionCount = useMemo(
    () => ads.filter((ad) => ["needs_attention", "ready_no_delivery", "expired"].includes(ad.delivery_status) || ad.issues.length > 0).length,
    [ads]
  );
  const filteredAds = useMemo(() => ads.filter((ad) => {
    if (filter === "delivering") return ad.delivery_status === "delivering";
    if (filter === "attention") return ["needs_attention", "ready_no_delivery", "expired"].includes(ad.delivery_status) || ad.issues.length > 0;
    if (filter === "pending") return ad.delivery_status === "pending_review";
    if (filter === "paused") return ["paused", "rejected"].includes(ad.delivery_status);
    return true;
  }), [ads, filter]);

  const handleToggleStatus = async (ad: Ad) => {
    setMutationId(ad.id);
    try {
      await apiPatch(`/api/admin/ads/${ad.id}/status`, { active: ad.active === 1 ? 0 : 1 });
      toast({ title: ad.active === 1 ? "광고를 중지했습니다" : "광고를 활성화했습니다" });
      await loadData(true);
    } catch (err) {
      toast({ title: "상태 변경 실패", description: err instanceof Error ? err.message : "다시 시도해 주세요.", variant: "destructive" });
    } finally {
      setMutationId(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      title: String(form.get("title") || ""),
      advertiserName: String(form.get("advertiserName") || ""),
      businessName: String(form.get("businessName") || ""),
      businessId: String(form.get("businessId") || ""),
      advertiserId: form.get("advertiserId") ? Number(form.get("advertiserId")) : null,
      contactEmail: String(form.get("contactEmail") || ""),
      imageUrl: String(form.get("imageUrl") || ""),
      targetUrl: String(form.get("targetUrl") || ""),
      placement: String(form.get("placement") || "feed"),
      region: String(form.get("region") || "ALL"),
      startDate: String(form.get("startDate") || ""),
      endDate: String(form.get("endDate") || ""),
      priority: Number(form.get("priority") || 5),
      billingStatus: String(form.get("billingStatus") || "untracked"),
      reviewStatus: String(form.get("reviewStatus") || "approved"),
      tagsJson: String(form.get("tagsJson") || "[]"),
      adminNotes: String(form.get("adminNotes") || ""),
      cpcPrice: Number(form.get("cpcPrice") || 0),
    };
    setMutationId(editingAd?.id || "new");
    try {
      if (editingAd) await apiPatch(`/api/admin/ads/${editingAd.id}`, payload);
      else await apiPost("/api/admin/ads", payload);
      toast({ title: editingAd ? "광고를 수정했습니다" : "광고를 등록했습니다" });
      setDialogOpen(false);
      setEditingAd(null);
      await loadData(true);
    } catch (err) {
      toast({ title: "광고 저장 실패", description: err instanceof Error ? err.message : "입력값을 확인해 주세요.", variant: "destructive" });
    } finally {
      setMutationId(null);
    }
  };

  const openCreate = () => { setEditingAd(null); setDialogOpen(true); };
  const openEdit = (ad: Ad) => { setEditingAd(ad); setDialogOpen(true); };

  return (
    <AdminPageLayout
      title="동네업체 광고 운영"
      description="신청·검토·결제·지역 타기팅·송출 상태와 실제 성과를 한곳에서 관리합니다."
      icon={<Megaphone className="h-8 w-8" />}
      loading={loading}
      error={error}
      headerActions={<>
        <Link href="/admin/billing"><Button variant="outline" size="sm"><CreditCard className="mr-2 h-4 w-4" />결제·구독</Button></Link>
        <Button variant="outline" size="sm" onClick={() => void loadData(true)} disabled={refreshing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />새로고침
        </Button>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingAd(null); }}>
          <DialogTrigger asChild><Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />광고 등록</Button></DialogTrigger>
          <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
            <DialogHeader><DialogTitle>{editingAd ? "동네업체 광고 수정" : "동네업체 광고 등록"}</DialogTitle></DialogHeader>
            <form key={editingAd?.id || "new"} onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="광고 제목" name="title" required defaultValue={editingAd?.title} />
                <Field label="광고주명" name="advertiserName" required defaultValue={editingAd?.advertiser_name} />
                <Field label="업체명" name="businessName" defaultValue={editingAd?.business_name || ""} />
                <Field label="업체 ID (업체록 연결)" name="businessId" defaultValue={editingAd?.business_id || ""} />
                <Field label="광고주 ID (구독 연결)" name="advertiserId" type="number" min="1" defaultValue={editingAd?.advertiser_id ? String(editingAd.advertiser_id) : ""} />
                <Field label="연락 이메일" name="contactEmail" type="email" defaultValue={editingAd?.contact_email || ""} />
                <Field label="우선순위 (1–10)" name="priority" type="number" min="1" max="10" defaultValue={String(editingAd?.priority || 5)} />
              </div>
              <Field label="배너 이미지 URL (공개 HTTPS)" name="imageUrl" type="url" required defaultValue={editingAd?.image_url} />
              <Field label="클릭 목적지 URL (공개 HTTPS)" name="targetUrl" type="url" required defaultValue={editingAd?.target_url} />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <SelectField label="노출 지면" name="placement" value={editingAd?.placement || "feed"} options={PLACEMENTS.map(([value, label]) => ({ value, label }))} />
                <SelectField label="대상 지역" name="region" value={editingAd?.region || "ALL"} options={REGIONS.map((value) => ({ value, label: value === "ALL" ? "호주 전체" : value }))} />
                <Field label="시작일" name="startDate" type="date" required defaultValue={editingAd?.start_date?.slice(0, 10)} />
                <Field label="종료일" name="endDate" type="date" required defaultValue={editingAd?.end_date?.slice(0, 10)} />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {editingAd && <SelectField label="검토 상태" name="reviewStatus" value={editingAd.review_status} options={[{ value: "pending", label: "검토 대기" }, { value: "approved", label: "승인" }, { value: "rejected", label: "거절" }]} />}
                <SelectField label="결제 상태" name="billingStatus" value={editingAd?.billing_status || "untracked"} options={[{ value: "untracked", label: "결제 연결 전" }, { value: "unpaid", label: "미결제" }, { value: "paid", label: "결제 완료" }, { value: "waived", label: "무상/제휴" }, { value: "refunded", label: "환불" }]} />
                <Field label="CPC 단가 (센트)" name="cpcPrice" type="number" min="0" defaultValue={String(editingAd?.cpc_price || 0)} />
              </div>
              <div><Label htmlFor="tagsJson">타기팅 태그 (JSON 배열)</Label><Textarea id="tagsJson" name="tagsJson" defaultValue={editingAd?.tags_json || "[]"} placeholder='["이민", "NSW"]' /></div>
              <div><Label htmlFor="adminNotes">운영 메모</Label><Textarea id="adminNotes" name="adminNotes" defaultValue={editingAd?.admin_notes || ""} placeholder="계약 조건, 소재 확인, 광고주 요청사항" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>취소</Button><Button type="submit" disabled={mutationId !== null}>{mutationId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{editingAd ? "수정 저장" : "승인 광고 등록"}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </>}
    >
      <div className="space-y-6" data-testid="page-admin-local-ads">
        {attentionCount > 0 && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>운영 확인이 필요한 광고가 {attentionCount}건 있습니다. 만료·노출 0·URL/지면 오류를 먼저 처리하세요.</AlertDescription></Alert>}
        {(stats?.paidWithoutAds || stats?.paymentRiskAds) ? <Alert><CreditCard className="h-4 w-4" /><AlertDescription>결제 후 광고가 아직 없는 구독 {stats?.paidWithoutAds || 0}건, 결제 위험 상태인데 활성 광고가 남은 구독 {stats?.paymentRiskAds || 0}건입니다. <Link href="/admin/billing" className="font-medium underline">결제·구독에서 확인</Link>하세요.</AlertDescription></Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard title="전체 광고" value={stats?.totalAds} icon={<Megaphone />} />
          <StatCard title="현재 송출 가능" value={stats?.deliveringAds} icon={<CheckCircle2 className="text-green-600" />} />
          <StatCard title="확인 필요" value={attentionCount} icon={<AlertTriangle className="text-amber-600" />} />
          <StatCard title="누적 노출" value={stats?.totalImpressions} icon={<Eye />} />
          <StatCard title="누적 CTR" value={`${((stats?.averageCTR || 0) * 100).toFixed(2)}%`} icon={<BarChart3 />} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <BreakdownCard title="지면별 운영" items={stats?.byPlacement || []} />
          <BreakdownCard title="지역별 운영" items={stats?.byRegion || []} />
        </div>

        <Card>
          <CardHeader className="space-y-4">
            <CardTitle>광고 송출·성과 관리</CardTitle>
            <div className="flex flex-wrap gap-2">
              {([[
                "all", `전체 ${ads.length}`
              ], ["delivering", `정상 송출 ${stats?.deliveringAds || 0}`], ["attention", `확인 필요 ${attentionCount}`], ["pending", `검토 대기 ${stats?.pendingReview || 0}`], ["paused", "중지·거절"]] as Array<[AdFilter, string]>).map(([value, label]) => <Button key={value} size="sm" variant={filter === value ? "default" : "outline"} onClick={() => setFilter(value)}>{label}</Button>)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-sm" data-testid="table-ads">
                <thead><tr className="border-b"><th className="p-2 text-left">광고·업체</th><th className="p-2 text-left">타기팅</th><th className="p-2 text-left">기간·결제</th><th className="p-2 text-left">송출 상태</th><th className="p-2 text-left">실제 성과</th><th className="p-2 text-left">관리</th></tr></thead>
                <tbody>
                  {filteredAds.length === 0 ? <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">해당 상태의 광고가 없습니다.</td></tr> : filteredAds.map((ad) => {
                    const ctr = ad.impressions > 0 ? (ad.click_count / ad.impressions) * 100 : 0;
                    return <tr key={ad.id} className="border-b align-top" data-testid={`ad-${ad.id}`}>
                      <td className="p-2"><div className="flex gap-3"><img src={ad.image_url} alt="" className="h-14 w-20 rounded border object-cover" /><div><div className="font-medium">{ad.title}</div><div className="text-xs text-muted-foreground">{ad.business_name || ad.advertiser_name}</div>{ad.contact_email && <div className="text-xs text-muted-foreground">{ad.contact_email}</div>}<a href={ad.target_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center text-xs text-primary">목적지 확인 <ExternalLink className="ml-1 h-3 w-3" /></a></div></div></td>
                      <td className="p-2"><div className="flex flex-wrap gap-1"><Badge variant="outline">{ad.region === "ALL" ? "호주 전체" : ad.region}</Badge><Badge variant="outline">{PLACEMENTS.find(([value]) => value === ad.placement)?.[1] || ad.placement}</Badge><Badge variant="outline">우선 {ad.priority}</Badge></div></td>
                      <td className="p-2 text-xs"><div>{ad.start_date?.slice(0, 10) || "-"} ~ {ad.end_date?.slice(0, 10) || "-"}</div><div className="mt-2"><Badge variant={ad.billing_status === "paid" ? "default" : "secondary"}>{ad.billing_status}</Badge></div></td>
                      <td className="p-2">{deliveryBadge(ad.delivery_status)}{ad.issues.length > 0 && <div className="mt-2 space-y-1 text-xs text-destructive">{ad.issues.map((issue) => <div key={issue}>• {issueLabels[issue] || issue}</div>)}</div>}{ad.admin_notes && <div className="mt-2 max-w-xs text-xs text-muted-foreground">메모: {ad.admin_notes}</div>}</td>
                      <td className="p-2"><div className="flex gap-3"><span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{formatNumber(ad.impressions)}</span><span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{formatNumber(ad.click_count)}</span><span>{ctr.toFixed(2)}%</span></div><div className="mt-1 text-xs text-muted-foreground">최근 7일 {formatNumber(ad.impressions_7d)} 노출 · {formatNumber(ad.clicks_7d)} 클릭</div><div className="mt-1 text-xs text-muted-foreground">최근 이벤트 {ad.last_event_at ? new Date(ad.last_event_at).toLocaleString("ko-KR") : "없음"}</div></td>
                      <td className="p-2"><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => openEdit(ad)}><Pencil className="mr-1 h-3 w-3" />수정</Button><Button size="sm" variant={ad.active === 1 ? "outline" : "default"} disabled={mutationId === ad.id} onClick={() => void handleToggleStatus(ad)}>{mutationId === ad.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}{ad.active === 1 ? "중지" : "활성"}</Button></div></td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageLayout>
  );
}

function Field({ label, name, ...props }: { label: string; name: string } & React.ComponentProps<typeof Input>) {
  return <div><Label htmlFor={name}>{label}</Label><Input id={name} name={name} {...props} /></div>;
}

function SelectField({ label, name, value, options }: { label: string; name: string; value: string; options: Array<{ value: string; label: string }> }) {
  return <div><Label htmlFor={name}>{label}</Label><select id={name} name={name} defaultValue={value} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>;
}

function StatCard({ title, value, icon }: { title: string; value: string | number | undefined; icon: React.ReactNode }) {
  return <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">{title}</CardTitle><span className="[&>svg]:h-4 [&>svg]:w-4">{icon}</span></CardHeader><CardContent><div className="text-2xl font-bold">{typeof value === "number" ? formatNumber(value) : value ?? "-"}</div></CardContent></Card>;
}

function BreakdownCard({ title, items }: { title: string; items: Array<{ name: string; ads: number; active: number; impressions: number; clicks: number }> }) {
  return <Card><CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent className="space-y-2">{items.length === 0 ? <p className="text-sm text-muted-foreground">데이터가 없습니다.</p> : items.slice(0, 8).map((item) => <div key={item.name} className="grid grid-cols-[1fr_auto_auto] gap-3 rounded border p-2 text-sm"><span>{item.name}</span><span>{formatNumber(item.active)}/{formatNumber(item.ads)} 활성</span><span className="text-muted-foreground">{formatNumber(item.impressions)} 노출 · {formatNumber(item.clicks)} 클릭</span></div>)}</CardContent></Card>;
}
